/**
 * useMilestones — B-2026-0075
 *
 * React Query hooks for athlete milestone/badge data.
 *
 * useMyMilestones()    — current user's full milestone list (includes private badges).
 * useUserMilestones()  — another user's public milestone list (no private badges).
 */

import { useQuery } from '@tanstack/react-query';
import { getMyMilestones, getUserMilestones } from '@/services/milestonesApi';
import { useAuth } from '@/contexts/AuthContext';

export const MILESTONES_QUERY_KEY = 'milestones' as const;

/** Fetch the calling user's full milestone list (includes streak/private badges). */
export function useMyMilestones() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [MILESTONES_QUERY_KEY, 'me', user?.id],
    queryFn: getMyMilestones,
    enabled: !!user?.id,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

/** Fetch another user's public milestone list (no private badges). */
export function useUserMilestones(uid: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [MILESTONES_QUERY_KEY, 'uid', uid],
    queryFn: () => getUserMilestones(uid!),
    enabled: !!user?.id && !!uid,
    staleTime: 60_000,
  });
}
